<template>
  <section class="seo-overview">
    <div class="wrapper">
      <h2 class="seo-overview__heading">
        Frequently asked questions
      </h2>
      <p class="seo-overview__intro">
        Site Palette is a website color palette extractor that lets you extract
        a color palette from any website. Open a page, hit Create palette in the
        extension, and get the colors from the website as an accurate color scheme
        with hex codes in seconds.
      </p>

      <ul class="seo-overview__faq">
        <li
          v-for="(item, i) in items"
          :key="i"
          class="faq-card"
          :class="{ 'faq-card--open': openIndex === i }"
        >
          <button
            class="faq-card__q"
            :aria-expanded="openIndex === i ? 'true' : 'false'"
            @click="toggle(i)"
          >
            <h3 class="faq-card__q-text">{{ item.q }}</h3>
            <span class="faq-card__icon" aria-hidden="true">
              <span class="faq-card__bar"></span>
              <span class="faq-card__bar faq-card__bar--v"></span>
            </span>
          </button>
          <div class="faq-card__a-wrap">
            <div class="faq-card__a-inner">
              <p class="faq-card__a">{{ item.a }}</p>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </section>
</template>

<script>
export default {
  data() {
    return {
      openIndex: 0,
      items: [
        {
          q: "How do I extract a color palette from a website?",
          a: "Install the Site Palette extension for Chrome, Firefox, or Safari, open any website, and hit Create palette in the extension. It instantly generates a color palette with hex codes from the page.",
        },
        {
          q: "How do I get colors from a website?",
          a: "Site Palette reads the page and pulls out every colour it uses, so you can get the color palette from a website without inspecting the CSS. You get the dominant colours, a full color scheme, and the hex codes for each one.",
        },
        {
          q: "Is Site Palette free?",
          a: "Site Palette comes with a free 24-hour trial. After that, you can keep unlimited access with a subscription from $2.50/mo (billed annually) or a one-time lifetime purchase for $99.",
        },
        {
          q: "Can I download the extracted color scheme?",
          a: "Yes. Every color palette can be shared via a permalink or downloaded as SVG, Sketch, and Adobe Swatch files, so you can drop it straight into your design workflow.",
        },
      ],
    };
  },
  methods: {
    toggle(i) {
      this.openIndex = this.openIndex === i ? null : i;
    },
  },
  mounted() {
    const schema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: this.items.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    };
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "faq-jsonld";
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);
  },
  unmounted() {
    const script = document.getElementById("faq-jsonld");
    if (script) script.remove();
  },
};
</script>

<style scoped>
.seo-overview {
  padding: 100px 0;
  background: #f5f8fa;
  color: #3d3d3d;
}

.wrapper {
  max-width: 1224px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 20px;
}

.seo-overview__heading {
  font-weight: 600;
  font-size: 48px;
  line-height: 62px;
  letter-spacing: -0.02em;
  color: #17191d;
  text-align: center;
  margin: 0 0 16px;
}

.seo-overview__intro {
  font-weight: 500;
  font-size: 18px;
  line-height: 26px;
  color: #17191d;
  text-align: center;
  max-width: 760px;
  margin: 0 auto 48px;
}

.seo-overview__faq {
  list-style: none;
  margin: 0 auto;
  padding: 0;
  max-width: 760px;
}

.faq-card {
  background: #ffffff;
  border: 1px solid rgba(41, 44, 46, 0.1);
  border-radius: 10px;
  margin-bottom: 12px;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;
}

.faq-card--open {
  border-color: rgba(118, 147, 252, 0.5);
  box-shadow: 0 4px 24px 0 rgba(0, 0, 0, 0.08);
}

.faq-card__q {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}

.faq-card__q-text {
  font-weight: 600;
  font-size: 17px;
  line-height: 24px;
  color: #17191d;
  margin: 0;
}

.faq-card__icon {
  position: relative;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
}

.faq-card__bar {
  position: absolute;
  top: 50%;
  left: 0;
  width: 16px;
  height: 2px;
  background: #7693fc;
  border-radius: 2px;
  transform: translateY(-50%);
}

.faq-card__bar--v {
  transition: transform 0.28s ease;
  transform: translateY(-50%) rotate(90deg);
}

.faq-card--open .faq-card__bar--v {
  transform: translateY(-50%) rotate(0deg);
}

.faq-card__a-wrap {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.28s ease;
}

.faq-card--open .faq-card__a-wrap {
  grid-template-rows: 1fr;
}

.faq-card__a-inner {
  overflow: hidden;
}

.faq-card__a {
  font-weight: 400;
  font-size: 16px;
  line-height: 26px;
  color: #51585f;
  margin: 0;
  padding: 0 24px 22px;
}

@media screen and (max-width: 600px) {
  .seo-overview {
    padding: 56px 0;
  }
  .wrapper {
    padding: 0 16px;
  }
  .seo-overview__heading {
    font-size: 36px;
    line-height: 44px;
  }
  .seo-overview__intro {
    font-size: 16px;
    margin-bottom: 32px;
  }
  .faq-card__q {
    padding: 18px 20px;
  }
  .faq-card__q-text {
    font-size: 16px;
  }
  .faq-card__a {
    font-size: 15px;
    line-height: 25px;
    padding: 0 20px 18px;
  }
}
</style>
